import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { publishedToastText } from '@/lib/template-admin';
import { STATUS_LABELS, STATUS_TONES } from '@/lib/template-labels';
import { toastIfSilent } from '@/lib/template-toast';
import { formTemplatesService, type AdminTemplateDetail, type StatsWindow } from '@/services/formTemplates';
import toastService from '@/services/toast/service';
import { TemplateImportsTab } from './components/TemplateImportsTab';
import { TemplateOverviewTab } from './components/TemplateOverviewTab';
import { TemplatePerformanceTab } from './components/TemplatePerformanceTab';
import { TemplateDetailSkeleton } from './components/TemplateSkeletons';

type DetailTab = 'overview' | 'performance' | 'imports';

const countPill =
  'ml-2 flex h-5 items-center justify-center rounded-full bg-primary/10 px-2 text-xs font-semibold text-primary';

const TemplateDetail = () => {
  const { id = '' } = useParams();
  const [days, setDays] = useState<StatsWindow>(90);
  const [detail, setDetail] = useState<AdminTemplateDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState<DetailTab>('overview');
  const [refreshing, setRefreshing] = useState(false);
  // Only the newest request may write, so a slow answer can't replace a newer one.
  const latestRequest = useRef(0);
  // The period of the numbers on screen; a failed refetch puts the picker back to it.
  const loadedDays = useRef<StatsWindow | null>(null);

  /**
   * Refetches the detail without clearing it, for the period asked for (default: the one on screen);
   * returns what was loaded (null when missing or failed).
   */
  const reload = useCallback(
    async (requested?: StatsWindow): Promise<AdminTemplateDetail | null> => {
      const request = ++latestRequest.current;
      setRefreshing(true);
      if (loadedDays.current === null) setLoadError(false);
      try {
        const loaded = await formTemplatesService.detail(id, requested ?? loadedDays.current ?? 90);
        if (request === latestRequest.current) {
          if (loaded === null) {
            loadedDays.current = null;
            setDetail(null);
            setNotFound(true);
          } else {
            loadedDays.current = loaded.days;
            setDetail(loaded);
            setDays(loaded.days);
            setNotFound(false);
            setLoadError(false);
          }
        }
        return loaded;
      } catch (err) {
        if (request === latestRequest.current) {
          // The api client toasted a server message; anything else gets the fallback.
          toastIfSilent(err, "Couldn't load this template");
          if (loadedDays.current === null) setLoadError(true);
          else setDays(loadedDays.current);
        }
        return null;
      } finally {
        if (request === latestRequest.current) setRefreshing(false);
      }
    },
    [id],
  );

  const changeDays = (next: StatsWindow) => {
    // A click on the pill that is already selected asks for nothing new.
    if (next === days) return;
    setDays(next);
    void reload(next);
  };

  /** Opens the tab during the click so popup blockers allow it, then points it at the builder. */
  const openBuilder = async () => {
    const tab = window.open('', '_blank');
    try {
      const { builderUrl } = await formTemplatesService.builderSession(id);
      if (tab) {
        tab.opener = null;
        tab.location.href = builderUrl;
      } else {
        window.location.assign(builderUrl);
      }
    } catch (err) {
      // The api client toasted a server message (e.g. the 409 one); anything else gets the fallback.
      toastIfSilent(err, "Couldn't open the builder");
      tab?.close();
    }
  };

  // The parent toasts, so the message survives the Overview tab remounting on the new updatedAtUtc.
  const onPublished = async (versionNo: number) => {
    const statusBefore = detail?.status ?? 'draft';
    const fresh = await reload();
    toastService.success(publishedToastText(versionNo, fresh?.status ?? statusBefore));
  };

  // Another template starts from a clean page.
  useEffect(() => {
    loadedDays.current = null;
    setDetail(null);
    setNotFound(false);
    setLoadError(false);
    setDays(90);
    setTab('overview');
  }, [id]);

  // First load.
  useEffect(() => {
    void reload();
    return () => {
      latestRequest.current++;
    };
  }, [reload]);

  if (notFound) {
    return (
      <div className="space-y-6 w-full pb-12">
        <div className="flex items-center gap-3">
          <BackLink />
        </div>
        <Card className="py-14 text-center text-sm text-muted-foreground">That template does not exist</Card>
      </div>
    );
  }

  if (detail === null && loadError) {
    return (
      <div className="space-y-6 w-full pb-12">
        <div className="flex items-center gap-3">
          <BackLink />
        </div>
        <Card className="flex flex-col items-center justify-center gap-3 py-8 text-sm text-muted-foreground">
          <span className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            Couldn't load this template
          </span>
          <Button variant="outline" size="sm" onClick={() => void reload()} disabled={refreshing}>
            Try again
          </Button>
        </Card>
      </div>
    );
  }

  if (detail === null) return <TemplateDetailSkeleton />;

  return (
    <div className="space-y-6 w-full pb-12">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <BackLink />
        <h1 className="min-w-0 truncate text-2xl sm:text-3xl font-bold tracking-tight">{detail.name}</h1>
        <Badge tone={STATUS_TONES[detail.status]}>{STATUS_LABELS[detail.status]}</Badge>
        <span className="text-sm text-muted-foreground">
          {detail.currentVersionNo === null
            ? 'No version yet'
            : detail.status === 'published'
              ? `Version ${detail.currentVersionNo} live`
              : `Version ${detail.currentVersionNo}, not live`}
        </span>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as DetailTab)} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="imports">
            Imports
            <span className={countPill}>{detail.imports.length}</span>
          </TabsTrigger>
        </TabsList>

        {/* Kept mounted so unsaved edits survive a visit to another tab. */}
        <TabsContent value="overview" forceMount className="data-[state=inactive]:hidden">
          <TemplateOverviewTab
            key={detail.updatedAtUtc}
            detail={detail}
            onChanged={reload}
            onOpenBuilder={openBuilder}
            onPublished={onPublished}
          />
        </TabsContent>

        <TabsContent value="performance">
          <TemplatePerformanceTab detail={detail} days={days} onDaysChange={changeDays} refreshing={refreshing} />
        </TabsContent>

        <TabsContent value="imports">
          <TemplateImportsTab imports={detail.imports} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

function BackLink() {
  return (
    <Button variant="outline" size="icon" className="h-9 w-9" asChild>
      <Link to="/templates" aria-label="Back to templates">
        <ArrowLeft className="h-4 w-4" />
      </Link>
    </Button>
  );
}

export default TemplateDetail;
